import { useRef, useState } from "react";
import { FloatButton } from "antd";
import { CommentOutlined } from "@ant-design/icons";
import { capturePageScreenshot, CapturedScreenshot } from "@/utils/capture_screenshot";
import FeedbackDialog from "./feedback_dialog";

/**
 * Globally visible feedback entry point, rendered once in the app shell so it
 * is available on every screen. The dialog opens immediately; the screenshot
 * is captured in the background and loads into the dialog's reserved preview
 * area (the capture filters out the open modal, so it never appears in the
 * shot).
 */
export default function FeedbackButton() {
  const [open, setOpen] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [screenshot, setScreenshot] = useState<CapturedScreenshot | null>(null);
  // Guards against a stale capture (dialog closed + reopened while the
  // previous capture was still running) overwriting the fresh one.
  const captureRun = useRef(0);

  const handleOpen = () => {
    if (open) return;
    setOpen(true);
    setScreenshot(null);
    setCapturing(true);
    const run = ++captureRun.current;
    void capturePageScreenshot().then((shot) => {
      if (captureRun.current !== run) return;
      setScreenshot(shot);
      setCapturing(false);
    });
  };

  return (
    <>
      <FloatButton
        type="primary"
        icon={<CommentOutlined />}
        // Placement "left" opens the tooltip toward the page interior. The
        // default (top) centers it above the button, which sits only 24px
        // from the viewport's right edge — the wider tooltip text then
        // overflows the body and pops in a layout-shifting horizontal
        // scrollbar on hover.
        tooltip={{ title: "Feedback geben", placement: "left" }}
        onClick={handleOpen}
      />
      <FeedbackDialog
        open={open}
        onClose={() => setOpen(false)}
        screenshot={screenshot}
        capturing={capturing}
      />
    </>
  );
}
